import { useEffect, useRef, forwardRef, useMemo } from 'react';
import { Button, Tooltip } from 'antd';
import { ReactComponent as LoaderSvg } from '../../assets/chat-loader.svg';
import { ReactComponent as SendButtonSvg } from '../../assets/send-button.svg';
import '../../components/AdminCopilotChat/CopilotInput.less';

interface PromptInputProps {
  className?: string;
  currentPrompt: string;
  cancellable?: boolean;
  isLoading?: boolean;
  isReadonly?: boolean;
  disabled?: boolean;
  readonlyTooltip?: string;
  placeholder?: string;
  onCurrentPromptChange: (content: string) => void;
  onSubmit: (content: string) => void;
  onShowingPromptTemplateChange: (show: boolean) => void;
}

const PromptInput = forwardRef<HTMLDivElement, PromptInputProps>(
  (props, _ref) => {
    const {
      className,
      currentPrompt,
      cancellable = true,
      isLoading = false,
      isReadonly = false,
      disabled = false,
      readonlyTooltip = '',
      placeholder = 'Enter your request',
      onCurrentPromptChange,
      onSubmit,
      onShowingPromptTemplateChange,
    } = props;

    const ref = useMemo(() => {
      if (_ref && typeof _ref !== 'function') {
        return _ref;
      }
      return null;
    }, [_ref]);

    const promptOptionsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (ref?.current) {
        ref?.current?.focus();
      }
      const handleClickOutside = (event: MouseEvent) => {
        if (
          promptOptionsRef.current &&
          !promptOptionsRef.current.contains(event.target as Node)
        ) {
          onShowingPromptTemplateChange(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // User submits prompt by pressing Enter key
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (currentPrompt.trim() && !isLoading) {
          handleSubmit();
        }
      }
    };

    const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
      const currentPromptValue = e.currentTarget.textContent || '';
      onCurrentPromptChange(currentPromptValue);
      if (currentPromptValue === '/') {
        onShowingPromptTemplateChange(true);
      } else {
        onShowingPromptTemplateChange(false);
      }
    };

    const handleSubmit = () => {
      onSubmit(currentPrompt.trim());
    };

    const onClickSubmitPrompt = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      handleSubmit();
    };

    const onClickInsideInput = (e: React.MouseEvent<HTMLDivElement>) => {
      if (ref?.current) {
        ref?.current?.focus();
      }
    };

    let isNotSubmittable;
    if (disabled || isReadonly) {
      isNotSubmittable = true;
    } else if (cancellable) {
      isNotSubmittable = isLoading ? false : !currentPrompt.trim();
    } else {
      isNotSubmittable = isLoading || !currentPrompt.trim();
    }
    const isNotEditable = disabled || isReadonly;

    return (
      <div
        className={`chat-input-wrapper ${className}`}
        onClick={onClickInsideInput}
      >
        <div
          ref={ref}
          contentEditable={isNotEditable ? false : 'plaintext-only'}
          translate="no"
          className="ProseMirror"
          id="prompt-textarea"
          data-virtualkeyboard="true"
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          suppressContentEditableWarning
          data-placeholder={placeholder}
        />
        <div className="chat-input-handler-container">
          <Tooltip
            title={readonlyTooltip}
            placement="right"
            destroyOnHidden={false}
          >
            <Button
              onClick={onClickSubmitPrompt}
              disabled={isNotSubmittable}
              className="send-button"
            >
              {isLoading ? <LoaderSvg /> : <SendButtonSvg />}
            </Button>
          </Tooltip>
        </div>
      </div>
    );
  }
);
PromptInput.displayName = 'PromptInput';

export default PromptInput;
